import {useEffect, useRef, useState} from 'react';
import { NavLink, Outlet, Link } from 'react-router-dom';
import { User, LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ThemeToggle } from './ThemeToggle';

export function SiteLayout() {
    const { user, logout } = useAuth();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <header className="bg-white shadow-sm border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between gap-4">
                        <Link to={user ? '/dashboard' : '/'} className="flex items-center gap-3">
                            <img src="/logo.png" alt="Resume Builder" className="h-8 w-8" />
                            <h1 className="text-2xl font-bold text-gray-900">Resume Builder</h1>
                        </Link>
                        <div className="flex items-center gap-3 justify-end">
                            <nav className="hidden lg:flex flex-wrap items-center gap-4 text-sm font-medium text-gray-600">
                                <NavLink
                                    to="/dashboard"
                                    className={({ isActive }) =>
                                        isActive ? 'text-primary-600' : 'hover:text-gray-900'
                                    }
                                >
                                    Dashboard
                                </NavLink>
                                <NavLink
                                    to="/apply"
                                    className={({ isActive }) =>
                                        isActive ? 'text-primary-600' : 'hover:text-gray-900'
                                    }
                                >
                                    Applying
                                </NavLink>
                                <NavLink
                                    to="/history"
                                    className={({ isActive }) =>
                                        isActive ? 'text-primary-600' : 'hover:text-gray-900'
                                    }
                                >
                                    History
                                </NavLink>
                            </nav>
                            <button
                                type="button"
                                className="btn btn-secondary flex items-center justify-center w-10 h-10 rounded-full p-0 lg:hidden"
                                onClick={() => setIsMobileNavOpen((open) => !open)}
                                aria-label="Toggle navigation"
                            >
                                {isMobileNavOpen ? <X size={18} /> : <Menu size={18} />}
                            </button>
                            <div className="hidden lg:flex items-center gap-3">
                                <ThemeToggle />
                                <div className="relative" ref={menuRef}>
                                    <button
                                        type="button"
                                        className="btn btn-secondary flex items-center justify-center w-10 h-10 rounded-full p-0"
                                        onClick={() => setIsMenuOpen((open) => !open)}
                                        aria-haspopup="menu"
                                        aria-expanded={isMenuOpen}
                                    >
                                        <User size={18} />
                                    </button>
                                    {isMenuOpen && (
                                        <div
                                            className="absolute right-0 mt-2 w-56 rounded-lg bg-white border border-gray-200 shadow-lg py-2 z-50"
                                            role="menu"
                                        >
                                            <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                                                {user?.email || 'Signed in'}
                                            </div>
                                            <NavLink
                                                to="/profile"
                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                Profile settings
                                            </NavLink>
                                            <button
                                                type="button"
                                                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                                onClick={() => {
                                                    setIsMenuOpen(false);
                                                    logout();
                                                }}
                                            >
                                                <LogOut size={16} />
                                                Logout
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {isMobileNavOpen && (
                <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true">
                    <button
                        type="button"
                        className="absolute inset-0 bg-black/40"
                        aria-label="Close navigation"
                        onClick={() => setIsMobileNavOpen(false)}
                    />
                    <div className="absolute right-0 top-0 h-full w-72 bg-white shadow-xl border-l border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-4">
                            <div className="text-sm font-semibold text-gray-900">Menu</div>
                            <button
                                type="button"
                                className="btn btn-secondary flex items-center justify-center w-10 h-10 rounded-full p-0"
                                onClick={() => setIsMobileNavOpen(false)}
                                aria-label="Close navigation"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <nav className="flex flex-col gap-2 text-sm font-medium text-gray-700">
                            <NavLink
                                to="/dashboard"
                                className={({ isActive }) =>
                                    `px-3 py-2 rounded-md ${isActive ? 'bg-gray-100 text-primary-600' : 'hover:bg-gray-50'}`
                                }
                                onClick={() => setIsMobileNavOpen(false)}
                            >
                                Dashboard
                            </NavLink>
                            <NavLink
                                to="/apply"
                                className={({ isActive }) =>
                                    `px-3 py-2 rounded-md ${isActive ? 'bg-gray-100 text-primary-600' : 'hover:bg-gray-50'}`
                                }
                                onClick={() => setIsMobileNavOpen(false)}
                            >
                                Applying
                            </NavLink>
                            <NavLink
                                to="/history"
                                className={({ isActive }) =>
                                    `px-3 py-2 rounded-md ${isActive ? 'bg-gray-100 text-primary-600' : 'hover:bg-gray-50'}`
                                }
                                onClick={() => setIsMobileNavOpen(false)}
                            >
                                History
                            </NavLink>
                        </nav>

                        <div className="mt-4 border-t border-gray-200 pt-4">
                            <div className="flex items-center justify-between">
                                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Theme</div>
                                <ThemeToggle />
                            </div>
                        </div>

                        <div className="mt-4 border-t border-gray-200 pt-4">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                Account
                            </div>
                            <div className="text-sm text-gray-700 mb-3 break-all">
                                {user?.email || 'Signed in'}
                            </div>
                            <NavLink
                                to="/profile"
                                className="block px-3 py-2 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                                onClick={() => setIsMobileNavOpen(false)}
                            >
                                Profile settings
                            </NavLink>
                            <button
                                type="button"
                                className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50 border border-red-100"
                                onClick={() => {
                                    setIsMobileNavOpen(false);
                                    logout();
                                }}
                            >
                                <LogOut size={16} />
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <main className="flex-1 p-6">
                <Outlet />
            </main>

            <footer className="bg-white border-t border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-sm text-gray-500">
                    (c) {new Date().getFullYear()} Resume Builder. All rights reserved.
                </div>
            </footer>
        </div>
    );
}
